import { Component } from '@theme/component';

/**
 * A custom element that manages the collections gender toggle.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} toggleButtons - The toggle buttons.
 * @property {HTMLElement[]} grids - The collection grids.
 *
 * @extends {Component<Refs>}
 */
class BbdCollections extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Initialize active gender from sessionStorage (do not persist changes from this section)
    requestAnimationFrame(() => {
      this.#initFromSession();
    });
  }

  #initFromSession() {
    let saved = null;
    try {
      saved = sessionStorage.getItem('bbd-header-drawer-menu-target');
    } catch (e) {
      // Ignore storage access errors (e.g., privacy mode)
    }

    const gender = saved === 'womens' || saved === 'mens' ? saved : 'mens';
    this.#setGender(gender);
  }

  /**
   * Switches the active gender view.
   * @param {Event} event
   */
  switchGender(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const gender = button.dataset.gender;
    if (!gender) return;

    this.#setGender(gender);
  }

  #setGender(gender) {
    // Update section attribute
    this.dataset.activeGender = gender;

    // Update buttons
    const buttons = this.#toArray(this.refs.toggleButtons);
    for (const btn of buttons) {
      const isActive = btn.dataset.gender === gender;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    }

    // Update grids
    const grids = this.#toArray(this.refs.grids);
    for (const grid of grids) {
      const isMatch = grid.dataset.genderGrid === gender;
      grid.hidden = !isMatch;
    }
  }
  #toArray(value) {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }
}

if (!customElements.get('bbd-collections')) {
  customElements.define('bbd-collections', BbdCollections);
}
